import { type KeyboardEventHandler } from 'react';

import { InputAdornment, TextField } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// Wrapper component: the search bar. Every list that can be searched uses it —
// care search, care circles, caseloads, the control panel's actions, calls and
// documents tabs, and the member app's webinars and help articles — so the
// adornment, the size and the pill radius are decided once rather than pasted
// per list. They were pasted, and only care search had the pill.
//
// The radius is a literal on the input because that is where production sets
// it (lib/llama's CareSearchFilters does the same) and there is no token for a
// full pill.
//
// Width belongs to the caller: every tab wraps this in a Box that owns it,
// because how wide a search bar is depends on the row it sits in.
const SearchField = ({
  placeholder,
  value,
  onChange,
  onKeyDown,
  size = 'xsmall',
}: {
  placeholder: string;
  /**
   * How tall it is. 'xsmall' is 30px, which is a dense desktop row: right in a
   * CAM tab's header, and too small to put a thumb on. A phone passes 'small'.
   */
  size?: 'xsmall' | 'small';
  /** Omitted where the tab has not wired filtering up yet. */
  value?: string;
  onChange?: (value: string) => void;
  /** Care search submits on Enter. The filtering tabs have nothing to submit. */
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}) => (
  <TextField
    slotProps={{
      input: {
        startAdornment: (
          <InputAdornment position='start'>
            <Icon icon='magnifying-glass' fontSize={16} />
          </InputAdornment>
        ),
        style: { borderRadius: 9999 },
      },
    }}
    fullWidth
    size={size}
    placeholder={placeholder}
    value={value}
    onChange={onChange && ((e) => onChange(e.target.value))}
    onKeyDown={onKeyDown}
  />
);

export default SearchField;
